import React, { useRef, useState, useEffect } from "react";
import { useTaskTimer } from "../hooks/useTaskTimer";
import useDeadlineCountdown from "../hooks/useDeadlineCountdown";
import NotesModal from "./NotesModal";
import TaskNameInput from "./TaskNameInput";
import TimerControls from "./TimerControls";
import ProgressBar from "./ProgressBar";
import DeadlinePicker from "./DeadlinePicker";
import TaskTags from "./TaskTags";
import TaskNotes from "./TaskNotes";
import TaskActions from "./TaskActions";
import TaskActionButton from "./TaskActionButton";
import TaskPrioritySelector from "./TaskPrioritySelector";
import { lang } from "../lang";
import "./Task.css"; // Import the new Task.css file

// Presentational component for rendering a single task
function Task({
  task,
  parentId,
  onAddTask,
  onUpdateTask,
  onDeleteTask,
  onAddChildTask,
  onSelectTask,
  isSelected,
  settings,
  taskRef,
  taskRefs,
  selectedPath,
}) {
  const {
    useTaskPriority,
    useNotes,
    useTags,
    tags: allSettingsTags,
    addTaskOnEnter,
    autoCompleteOnTimer,
    defaultTaskDuration,
  } = settings || {};

  const { displayRemaining, start, pause, reset } = useTaskTimer(
    task,
    onUpdateTask,
    autoCompleteOnTimer
  );
  const countdown = useDeadlineCountdown(task);
  const [showNotesModal, setShowNotesModal] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      // console.log(`[Task.js ID: ${task.id}] Received settings.useTaskPriority: ${useTaskPriority}`);
    }
  }, [useTaskPriority, task.id]);

  const handleNotesButtonClick = (e) => {
    setShowNotesModal(true);
  };

  const handleNotesSave = (notes) => {
    onUpdateTask(task.id, { notes: notes.slice(0, 500) });
    setShowNotesModal(false);
  };

  const handleCompleteChange = (e) => {
    e.stopPropagation();
    const isCompleted = e.target.checked;
    onUpdateTask(task.id, { completed: isCompleted });
  };

  const formatTime = (seconds) => {
    const currentDefaultDuration = defaultTaskDuration || 1800;
    const validSeconds = Number.isFinite(seconds)
      ? seconds
      : task.duration
      ? task.duration
      : currentDefaultDuration;
    if (!Number.isFinite(validSeconds)) {
      return "00:00:00";
    }
    const hours = Math.floor(validSeconds / 3600);
    const minutes = Math.floor((validSeconds % 3600) / 60);
    const secs = Math.floor(validSeconds % 60);
    return `${hours < 10 ? "0" : ""}${hours}:${
      minutes < 10 ? "0" : ""
    }${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleBackgroundClick = (e) => {
    if (
      e.target.closest(
        ".task-priority-selector-container, .task-action-button, input, textarea, select, .priority-dropdown-menu"
      )
    ) {
      return;
    }
    e.stopPropagation();
    if (task.children && task.children.length > 0) {
      onSelectTask(task.id);
    }
  };

  const handlePriorityChange = (newPriority) => {
    onUpdateTask(task.id, { priority: newPriority });
  };

  const isLeafDisplay = !task.children || task.children.length === 0;

  return (
    <div
      ref={typeof taskRef === "function" ? taskRef : null}
      className={`task ${task.isRest ? "rest-task" : ""} ${
        isSelected && task.children && task.children.length > 0
          ? "has-children-selected"
          : ""
      } ${task.completed ? "completed" : ""} ${
        (task.hasDeadline || task.isDeadline) &&
        task.deadline &&
        new Date(task.deadline) < new Date() &&
        !task.completed
          ? "overdue"
          : ""
      } ${task.isDaily ? "daily-task" : ""}`}
      onClick={handleBackgroundClick}
    >
      <div className="task-content">
        <div className="task-visible">
          <div className="task-input-container">
            <div className="task-name-and-checkbox-wrapper">
              <input
                type="checkbox"
                checked={!!task.completed}
                onChange={handleCompleteChange}
                onClick={(e) => e.stopPropagation()}
              />
              {useTaskPriority && (
                <TaskPrioritySelector
                  currentPriority={task.priority}
                  onPriorityChange={handlePriorityChange}
                />
              )}
              <TaskNameInput
                task={task}
                onUpdateTask={onUpdateTask}
                addTaskOnEnter={isLeafDisplay ? addTaskOnEnter : false}
                parentId={parentId}
                onAddTask={onAddTask}
              />
            </div>
            {isLeafDisplay &&
              !task.completed &&
              !task.isRest &&
              !task.hasDeadline &&
              !task.isDeadline && (
                <div className="timer-and-progress-wrapper">
                  {" "}
                  {/* Wrapper for timer and progress */}
                  <div className="timer numeric-timer">
                    {formatTime(displayRemaining)}
                  </div>
                  <ProgressBar
                    task={task}
                    displayRemaining={displayRemaining}
                  />
                </div>
              )}
          </div>
          {/* Deadline timer is shown outside the above wrapper if it's a deadline task */}
          {isLeafDisplay &&
            !task.completed &&
            !task.isRest &&
            (task.isDeadline || task.hasDeadline) &&
            task.deadline && (
              <div className="timer deadline-timer">
                {`${countdown.days} ${lang.task.days}, ${countdown.hours} ${lang.task.hours}`}
              </div>
            )}
        </div>

        <div className="task-hover">
          <div className="task-hover-inner">
            {isLeafDisplay && !task.isRest && (
              <TimerControls
                task={task}
                start={start}
                pause={pause}
                reset={reset}
                onUpdateTask={onUpdateTask}
                autoCompleteOnTimer={autoCompleteOnTimer}
              />
            )}
            <div className="task-controls">
              {task.isDeadline === true && (
                <DeadlinePicker task={task} onUpdateTask={onUpdateTask} />
              )}
              <TaskActions
                task={task}
                isChildTask={!isLeafDisplay}
                onDeleteTask={onDeleteTask}
                onAddChildTask={onAddChildTask}
                onSelectTask={onSelectTask}
                defaultTaskDuration={defaultTaskDuration}
                taskRefs={taskRefs}
              />
              {useNotes && !task.isRest && (
                <TaskActionButton
                  className="notes-button task-action-button-text"
                  onClick={handleNotesButtonClick}
                  title={lang.task.notesAlt}
                >
                  {lang.task.notesAlt}
                </TaskActionButton>
              )}
            </div>
            {useTags && !task.isRest && (
              <TaskTags
                task={task}
                tags={allSettingsTags}
                useTags={useTags}
                onUpdateTask={onUpdateTask}
              />
            )}
            {useNotes && !task.isRest && (
              <TaskNotes notes={task.notes} useNotes={useNotes} />
            )}
          </div>
        </div>
      </div>
      {useNotes && !task.isRest && (
        <NotesModal
          isOpen={showNotesModal}
          notes={task.notes || ""}
          onSave={handleNotesSave}
          onCancel={() => setShowNotesModal(false)}
        />
      )}
    </div>
  );
}

export default Task;
